/* eslint-disable @typescript-eslint/no-explicit-any */
import { KeyboardEvent, Dispatch, SetStateAction, useState, FocusEvent } from 'react';
import { User } from '../../../store/User';
import { EditFieldType } from './LabelValueField';
import { UpstackAutocomplete } from './Inputs/AutoComplete';
import { TextEditComponent } from './Inputs/TextEditComponent';
import { SelectEditComponent } from './Inputs/SelectEditComponent';
import { DateEditComponent } from './Inputs/DateEditComponent';
import { MultiSelect } from './Inputs/MultiSelect';
import { CurrencyEditComponent } from './Inputs/CurrencyEditComponent';
import { SearchBoxEditComponent } from './Inputs/SearchBoxEditComponent';
import { CheckboxEditComponent } from './Inputs/CheckboxEditComponent';

export interface InputMeta {
  val: string | number | null | boolean;
  label: string;
  handler: (v: string | number | null | any, d?: string) => void;
  options?: any[];
  change?: (v: string | number | null) => void;
  mapType?: string;
}

export interface EditComponentParams {
  type: EditFieldType;
  inputMeta: InputMeta;
  setMode: Dispatch<SetStateAction<'read' | 'edit'>>;
}

export type InputType = string | number | null | Date | User | undefined | boolean | string[];

export const EditComponent = ({ type, inputMeta, setMode }: EditComponentParams) => {
  const { label, val, handler, options, change, mapType } = inputMeta;
  const [localVal, setLocalVal] = useState<InputType>(val);

  const handleSave = (
    value: InputType,
    e?:
      | KeyboardEvent<HTMLDivElement>
      | KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
      | FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    displayValue?: string
  ) => {
    let newVal = e ? localVal : value;
    if (newVal instanceof Array) {
      if (newVal.filter((v) => v === null || v === '').length > 0) newVal = [];
      newVal = newVal.join(';');
    }
    setLocalVal(newVal);
    const updateValue = type === EditFieldType.CURRENCY && newVal ? Number(newVal) : newVal;
    handler(updateValue, displayValue);
  };

  const handleClose = () => {
    setMode('read');
  };

  const componentMap = {
    [EditFieldType.TEXT]: (
      <TextEditComponent
        isTextarea={false}
        {...{ label, handleSave, handleClose, change, localVal, setLocalVal }}
      />
    ),
    [EditFieldType.TEXTAREA]: (
      <TextEditComponent
        isTextarea={true}
        {...{ label, handleSave, handleClose, change, localVal, setLocalVal }}
      />
    ),
    [EditFieldType.SELECT]: <SelectEditComponent {...{ label, localVal, handleSave, handleClose, options }} />,
    [EditFieldType.MULTI_SELECT]: (
      <MultiSelect
        localValue={typeof localVal === 'string' && localVal ? localVal.split(';') : []}
        options={options}
        handleSave={handleSave}
        onClose={handleClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSave(undefined, e);
          }
          if (e.key === 'Esc') handleClose();
        }}
      />
    ),
    [EditFieldType.DATE]: <DateEditComponent {...{ label, val, handler, handleClose, setLocalVal }} />,
    [EditFieldType.CURRENCY]: <CurrencyEditComponent {...{ label, localVal, handleSave, setLocalVal, change }} />,
    [EditFieldType.AUTOCOMPLETE]: (
      <UpstackAutocomplete
        onChange={(e, v) => handleSave(v)}
        value={String(localVal) || ''}
        options={options || []}
      />
    ),
    [EditFieldType.SEARCHBOX]: <SearchBoxEditComponent {...{ localVal, handleSave, handleClose, mapType }} />,
    [EditFieldType.CHECKBOX]: <CheckboxEditComponent {...{ localVal, handleSave, handleClose }} />,
    [EditFieldType.PERCENT]: <div></div>
  };

  return componentMap[type] || <div></div>;
};
